import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { faSearch, faTimes, faPlus, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { operationType, selectDataType } from "../../../../../services/types";
import { Main, Styled } from "./style";
import { updateNodeValue } from "../../../../../services/method";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { Select } from "../../../../form/select";
import axios from "axios";
import { webURL } from "../../../../../services/API";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";

type UrlSearchProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

interface UrlLibraryItem {
   _id: string;
   url: string;
   name: string;
   scrapeMode: 'normal' | 'vlm';
}

export function UrlSearch({ id, data, setChildMinimize, setChildSettings }: UrlSearchProps) {
   const userToken = useSelector((state: RootState) => state.user.data?.token);
   const { setNodes } = useReactFlow();
   // const inputRef = React.useRef<HTMLInputElement>(null);
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [draftUrl, setDraftUrl] = React.useState('');
   const [urlLibrary, setUrlLibrary] = React.useState<UrlLibraryItem[]>([]);
   const [showLibraryDropdown, setShowLibraryDropdown] = React.useState(false);

   // Scrape mode state
   const [scrapeModes, setScrapeModes] = React.useState<selectDataType[]>([
      { id: 1, name: 'normal', isActive: true },
      { id: 2, name: 'vlm', isActive: false },
   ]);

   // Cache toggle state (default: enabled)
   const [enableCache, setEnableCache] = React.useState(true);

   // Scroll count state (default: 7)
   const [scrollCount, setScrollCount] = React.useState<number>(7);

   // VLM screenshots state (default: 10)
   const [vlmScreenshots, setVlmScreenshots] = React.useState<number>(10);

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // Initialize scrapeMode and fetch URL library
   React.useEffect(() => {
      if (!data.value?.scrapeMode) {
         updateNodeValue(id, setNodes, {
            urls: data.value?.urls || [],
            scrapeMode: 'normal'
         });
      } else {
         setScrapeModes(modes => modes.map(m => ({
            ...m,
            isActive: m.name === data.value.scrapeMode
         })));
      }

      // Initialize cache toggle from saved value
      if (data.value?.enableCache !== undefined) {
         setEnableCache(data.value.enableCache);
      }

      // Initialize scroll count from saved value
      if (data.value?.scrollCount !== undefined) {
         setScrollCount(data.value.scrollCount);
      }

      // Initialize VLM screenshots from saved value
      if (data.value?.vlmScreenshots !== undefined) {
         setVlmScreenshots(data.value.vlmScreenshots);
      }

      // Fetch URL library
      (async () => {
         try {
            const response = await axios.get(`${webURL}/web-scraper/urls`, {
               headers: { 'x-auth-token': userToken }
            });
            setUrlLibrary(response.data);
         } catch (err) {
            console.error('Failed to fetch URL library:', err);
         }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleSelectScrapeMode = (selectId: number) => {
      setScrapeModes(modes => modes.map(m => ({
         ...m,
         isActive: m.id === selectId
      })));
      const selectedMode = scrapeModes.find(m => m.id === selectId)?.name;
      updateNodeValue(id, setNodes, {
         ...data.value,
         scrapeMode: selectedMode
      });
   };

   // Handle cache toggle
   const handleToggleCache = () => {
      const newValue = !enableCache;
      setEnableCache(newValue);
      updateNodeValue(id, setNodes, {
         ...data.value,
         enableCache: newValue
      });
   };

   // Handle scroll count change
   const handleScrollCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value);
      const newValue = isNaN(val) ? 0 : Math.max(0, Math.min(50, val));
      setScrollCount(newValue);
      updateNodeValue(id, setNodes, {
         ...data.value,
         scrollCount: newValue
      });
   };

   // Handle VLM screenshots change
   const handleVlmScreenshotsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value);
      const newValue = isNaN(val) ? 1 : Math.max(1, Math.min(50, val));
      setVlmScreenshots(newValue);
      updateNodeValue(id, setNodes, {
         ...data.value,
         vlmScreenshots: newValue
      });
   };

   // ---
   const draftUrlWarning = React.useMemo(() => {
      const rawValue = draftUrl.trim();
      if (!rawValue) return '';

      try {
         const parsedUrl = new URL(rawValue);
         if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
            return 'Prefer http/https URLs to avoid unsupported or unsafe schemes.';
         }
         if (parsedUrl.username || parsedUrl.password) {
            return 'Avoid embedding credentials directly in URL.';
         }
         return '';
      } catch {
         return 'URL appears malformed. It can still be saved, but scraping may fail.';
      }
   }, [draftUrl]);

   // ---
   const submitDraftUrl = () => {
      const value = draftUrl.trim();

      if (value) {
         updateNodeValue(id, setNodes, {
            ...data.value,
            urls: data.value?.urls ? [...data.value.urls, value] : [value]
         });
         setDraftUrl('');
      }
   };

   const handleAddURL = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!['Enter', 'NumpadEnter'].includes(event.code)) return;
      submitDraftUrl();
   }

   // Add URL from library
   const handleAddFromLibrary = (item: UrlLibraryItem) => {
      // Check if URL already exists
      if (data.value?.urls?.includes(item.url)) {
         setShowLibraryDropdown(false);
         return;
      }

      updateNodeValue(id, setNodes, {
         ...data.value,
         urls: data.value?.urls ? [...data.value.urls, item.url] : [item.url]
      });
      setShowLibraryDropdown(false);
   }

   // ---
   const handleRemove = (index: number) => {
      setNodes((nodes) => nodes.map((node) => {
         if (node.id === id) {
            const arr = [...node.data.value.urls];
            arr.splice(index, 1);
            return { ...node, data: { ...node.data, value: { ...node.data.value, urls: arr } } };
         } else return node;
      }));
   }

   // ---
   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ width: '400px' }}
         ><Styled>
               <div className="config-card">
                  <Select
                     title="Scrape Mode"
                     data={scrapeModes}
                     handleSelectItem={handleSelectScrapeMode}
                     isSmall
                  />

                  {/* Settings Row */}
                  <div className="settings-row">
                     {/* Always Rescrape Toggle */}
                     <div className="cache-toggle">
                        <span className="cache-label">Always Rescrape</span>
                        <div
                           className={`toggle-switch ${!enableCache ? 'active' : ''}`}
                           onClick={handleToggleCache}
                        >
                           <div className="toggle-slider" />
                        </div>
                     </div>

                     {/* Scroll Count Input — only for normal mode */}
                     {scrapeModes.find(m => m.name === 'normal')?.isActive && (
                        <div className="scroll-count">
                           <span>Scroll Count</span>
                           <input
                              type="number"
                              min="0"
                              max="50"
                              value={scrollCount}
                              onChange={handleScrollCountChange}
                              placeholder="7"
                           />
                        </div>
                     )}

                     {/* VLM Screenshots Input — only for VLM mode */}
                     {scrapeModes.find(m => m.name === 'vlm')?.isActive && (
                        <div className="scroll-count">
                           <span>Screenshots</span>
                           <input
                              type="number"
                              min="1"
                              max="50"
                              value={vlmScreenshots}
                              onChange={handleVlmScreenshotsChange}
                              placeholder="10"
                           />
                        </div>
                     )}
                  </div>
               </div>
               <div className="add-container">
                  <div className="add">
                     <input
                        value={draftUrl}
                        placeholder="Enter URL here"
                        onChange={(event) => setDraftUrl(event.target.value)}
                        onKeyUp={handleAddURL}
                     />
                  </div>
                  <button className="add-action-btn" onClick={submitDraftUrl}>
                     Add
                  </button>
               </div>
               {draftUrlWarning ? (
                  <div style={{ marginTop: -8, marginBottom: 12, color: '#8a5b00', backgroundColor: '#fff4e5', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>
                     {draftUrlWarning}
                  </div>
               ) : null}
               {/* URL Library Section */}
               <div className="library-section">
                  <div
                     className="library-button"
                     onClick={() => setShowLibraryDropdown(!showLibraryDropdown)}
                  >
                     <FontAwesomeIcon icon={faPlus} />
                     <span>Add from Library ({urlLibrary.length})</span>
                  </div>
                  {showLibraryDropdown && urlLibrary.length > 0 && (
                     <div className="library-dropdown">
                        {urlLibrary.map(item => (
                           <div
                              key={item._id}
                              className="library-item"
                              onClick={() => handleAddFromLibrary(item)}
                           >
                              <span className="library-url">{item.url}</span>
                              <span className={`library-mode ${item.scrapeMode}`}>
                                 {item.scrapeMode.toUpperCase()}
                              </span>
                           </div>
                        ))}
                     </div>
                  )}
                  {showLibraryDropdown && urlLibrary.length === 0 && (
                     <div className="library-empty">
                        No URLs in library. Add URLs from Web Scraper Studio.
                     </div>
                  )}
               </div>

               <div className="items">
                  <div className="title">
                     <h4>URLs:</h4>
                     {!data.value?.urls?.length ? <span>There is nothing</span> : null}
                  </div>
                  <ul>{data.value?.urls?.map((name: string, index: number) => (
                     <li key={index.toString()}>
                        <div className="url-content">
                           <FontAwesomeIcon icon={faGlobe} className="url-icon" />
                           <span>{name}</span>
                        </div>
                        <span className="remove" onClick={_ => handleRemove(index)}><FontAwesomeIcon icon={faTimes} /></span>
                     </li>
                  ))}</ul>
               </div>
            </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}